/**
*
*   Reviews
*       - The Reviews Components
*
**/
'use client';


import Config from '@/config';
import moment from 'moment-timezone';
import Button from '@/components/button';
import { header } from '@/components/header/wrapper';
import { ModalContext } from '@/context/modal';
import { ReviewsContext } from '@/context/reviews';
import RewardsHook from '@/libraries/rewards';
import Preloader from '@/components/preloader';
import { useSession } from 'next-auth/react';
import { useRef, useState, useContext } from 'react';
import { useLocale, useTranslations } from 'next-intl';

//Build the Element
export default function ReviewsComponent({ name , identifier , reference , props }){

	const t = useTranslations();
	const locale = useLocale();
	const ref = useRef();

	//The Session
	const { data: session, status } = useSession();

	//Modal Object
	const { Modal } = useContext(ModalContext);

	//Reviews
	const { Reviews } = useContext(ReviewsContext);

	//The Current Page
	const [ page , setPage ] = useState(0);

	//The Current Sort
	const [ sort , setSort ] = useState('');

	//Pagination & Sorting State
	const [ loading , setLoading ] = useState(false);

	//The Rewards
	const Rewards = RewardsHook();

	//Review Object
	const Component = {

		//SVG Coordinates
		coords: [
			'M5.30303 0.5L3.6643 3.7917L0 4.31946L2.65152 6.88191L2.02545 10.5L5.30303 8.7917L8.58049 10.5L7.95455 6.88191L10.6061 4.31946L6.94176 3.7917L5.30315 0.5H5.30303Z',
			'M17.9293 0.5L16.2905 3.7917L12.6262 4.31946L15.2777 6.88191L14.6518 10.5L17.9293 8.7917L21.2067 10.5L20.5808 6.88191L23.2323 4.31946L19.5681 3.7917L17.9294 0.5H17.9293',
			'M30.5553 0.5L28.9167 3.7917L25.2524 4.31946L27.9038 6.88191L27.2779 10.5L30.5553 8.7917L33.8329 10.5L33.207 6.88191L35.8585 4.31946L32.1942 3.7917L30.5555 0.5H30.5553',
			'M43.1816 0.5L41.543 3.7917L37.8787 4.31946L40.5301 6.88191L39.9041 10.5L43.1816 8.7917L46.4592 10.5L45.8332 6.88191L48.4847 4.31946L44.8204 3.7917L43.1817 0.5H43.1816',
			'M55.8082 0.5L54.1694 3.7917L50.5051 4.31946L53.1566 6.88191L52.5306 10.5L55.8082 8.7917L59.0856 10.5L58.4597 6.88191L61.1112 4.31946L57.4469 3.7917L55.8083 0.5H55.8082'
		],

		//Total reviews per page
		perPage: 5,

		/**
		*
		*  add
		*  - Open the modal for adding a review
		*
		*   Params:
		*       n/a
		*
		**/
		add: () => Modal.open({
			reviews: {
				context:  Reviews,
				callback: () => {

					//Reset the Sort to Default
					setSort('');

				}
			}
		}),

		/**
		*
		*  edit
		*  - Open the modal for editing a review
		*
		*   Params:
		*       id:     (INT) The Review ID
		*
		**/
		edit: (id) => Modal.open({
			reviews: {
				context:  Reviews,
				reviewid: id,
				callback: () => {

					//Reset the Sort to Default
					setSort('');

				}
			}
		}),

		/**
		*
		*  get
		*  - Get the Reviews for the page
		*
		*   Params:
		*       n/a
		*
		**/
		get: () => {
			const chunks = [];
			for( let i = 0, len = Reviews.total; i < len; i += Component.perPage ) chunks.push(
				Reviews.all.slice( i , i + Component.perPage )
			);
			return chunks[ page ];
		},

		/**
		*
		*  pages
		*  - Get the Total Pages
		*
		*   Params:
		*       n/a
		*
		**/
		pages: () => ( Reviews.total > 0 ? ( Reviews.total / Component.perPage ) : 0 ),

		/**
		*
		*  goTo
		*  - Go to a specific page and scroll to the top of the list
		*
		*   Params:
		*       - page  (INT) The Page Number
		*
		**/
		goTo: (page) => {

			//Set loading + update page
			setLoading(true);

			//Set page
			setPage(page);

			//Scroll up
			Component.scroll( () => setLoading(false) );

		},

		//Go to next page
		next: () => Component.goTo( page + 1 ),
		prev: () => Component.goTo( page - 1 ),
		page: (val) => Component.goTo( val ),

		/**
		*
		*  scroll
		*  - Locks the navigation and scrolls to the top of the reviews
		*
		*   Params:
		*       n/a
		*
		**/
		scroll: () => {

			const $header = header.current;

			//Lock the Navigation
			$header.classList.add('locked');

			//Scroll to Top
			window.setTimeout(
				() => window.scrollTo({
					top:        ref.current.getBoundingClientRect().top + window.scrollY - 100,
					behavior:   'smooth'
				})
			, 200);

			//Stop Loading
			window.setTimeout( () => {
				$header.classList.remove('locked');
				$header.classList.add('scroll');
				setLoading(false);
			}, 1000 );

		},

		/**
		*
		*  sort
		*  - Sorts the Reviews based on the provided context
		*
		*   Params:
		*       - value:        (String) THe Sort Type
		*
		**/
		sort: (value) => {

			//The Loading
			setLoading(true);

			switch( value ){
				case 'rating':
					Reviews.sort( (a,b) => {

						//If it's a higher rating
						if( a.rating > b.rating ) return -1;

						//If it's a lower rating
						if( a.rating < b.rating ) return 1;

						//Just standard sort
						return b.id - a.id;

					});
					break;

				case 'helpfulness':
					Reviews.sort( (a,b) => {

						//More No's
						if( a.helpful.no > b.helpful.no ) return 1;

						//Less "No's"
						if( a.helpful.no < b.helpful.no ) return -1;

						//Less Yes's
						if( a.helpful.yes < b.helpful.yes ) return 1;

						//More "Yes" amounts
						if( a.helpful.yes > b.helpful.yes ) return -1;

						//Equal
						return 0;

					});
					break;

				default:
					Reviews.sort( (a,b) => b.id - a.id );
			}

			//Switch back to the first page
			setPage(0);

			//Set the Sort
			setSort(value);

			//Scroll Up
			Component.scroll( () => setLoading(false) );

		}

	};

	//Have the Points been rewarded?
	const rewarded = Rewards.awarded( `review:${  Reviews.reference }` );

	//Return
	return (
		<section id="reviews" ref={ ref } className="container">
			<div className="wrap">
				<header>
					<div>
						<Button onClick={
							() => Component.add()
						}>{ t('Components.Reviews.write') }</Button>
						<p className="note">
							<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
								<path d="M32 64V448H416V64H32zM0 32H32 416h32V64 448v32H416 32 0V448 64 32zM160 352h16 32V256H184 168V224h16 40 16v16V352h32 16v32H272 176 160V352zm88-216v48H200V136h48z"/>
							</svg>
							{ rewarded && (
								t('Components.Reviews.points.rewarded',{
									name: name
								})
							)}
							{ !rewarded && status != 'loading' && (
								t.rich(`Components.Reviews.points.${ status }`,{
									...Config.i18n.html,
									points: Config.rewards.review.value
								})
							)}
						</p>
					</div>
					<div>
						<h2>{
							t.rich('Components.Reviews.title', {
								...Config.i18n.html,
								name: name
							})
						}</h2>
						<div className="rating">
							{ Reviews.loading.output(
								<>
									{ Reviews.rating( Reviews.average() ) }
									<span>{
										t('Components.Reviews.total',{
											...Config.i18n.html,
											average:    Reviews.average(),
											total:      Reviews.total
										})
									}</span>
								</>
							)}
						</div>
					</div>
					<div>
						<select name="sort" value={ sort } onChange={
							(e) => Component.sort(e.target.value, reviews)
						}>
							<option value="">{ t('Components.Reviews.sort.title') }</option>
							<option value="rating">{ t('Components.Reviews.sort.rating') }</option>
							<option value="helpfulness">{ t('Components.Reviews.sort.helpful') }</option>
						</select>
					</div>
				</header>
				<div className="reviews">
					<Preloader status={ ( Reviews.loading.state || loading ) } sticky />
					{ Reviews.all.length == 0 && (
						<div className="none">
							<p>{ t('Components.Reviews.none') }</p>
							<Button onClick={ () => Component.add() } type="small">{ t('Components.Reviews.write') }</Button>
						</div>
					)}
					{ Reviews.all.length > 0 && Component.get().map( ( review , i ) => {

						//Get the helpful state
						const helpful = Reviews.helpful.get( review );

						return (
							<figure key={i}>
								<figcaption>
									<span className="name">{ review.customer }</span>
									{ review.city && (
										<span className="location">{ review.city }</span>
									)}
									<span className="postdate">{ moment( review.date ).format('MM/DD/YY') }</span>
								</figcaption>
								<blockquote>
									<div className="rating">
										{ Reviews.rating( review.rating ) }
									</div>
									<h3>{ review.title }</h3>
									<q>{ review.body }</q>
									<div className="actions">
										<strong>{ t('Components.Reviews.helpful.title') }</strong>
										<div>
											<Button disabled={ review.loading } onClick={ () => Reviews.helpful.set( review , 'yes' ) } size="narrow" inverted={ helpful != 'yes' }>
												{ t('Components.Reviews.helpful.yes') }
												{ review.loading && ( <small> <Preloader status={ review.loading } mini /></small>)}
												{ !review.loading && review.helpful.yes > 0 && ( <small> ({ review.helpful.yes })</small>)}
											</Button>
											<Button disabled={ review.loading } onClick={ () => Reviews.helpful.set( review , 'no')  } size="narrow" inverted={ helpful != 'no' }>
												{ t('Components.Reviews.helpful.no') }
												{ review.loading && ( <small> <Preloader status={ review.loading } mini /></small>)}
												{ !review.loading && review.helpful.no > 0 && ( <small> ({ review.helpful.no })</small>)}
											</Button>
										</div>
									</div>
								</blockquote>
								<div>

								</div>
							</figure>
						);
					})}
					{ Reviews.all.length > 5 && (
						<div className="buttons pagination">
							<Button size="narrow" onClick={()=>Component.prev()} disabled={ page == 0 } arrow="reverse">{ t('Navigation.prev') }</Button>

							{
								(()=>{
									return [...Array( Component.pages() > 5 ? 5 : Math.ceil( Component.pages() ) )];
								})().map( ( link , i ) => {

									const num = (
										//If we ahve enough pages for multi-page pagination
										page > 3 && Component.pages() > 5 ?

											//If less than the middle page
											i <= 1 ?
												( page - ( 2 - i ) )
											:
												//If greater than the middle page
												i >= 3 ?
													( page + ( i - 2 ) )
												:
													//The middle page is the current page
													page
										:
											//Just show the available pages
											i
									);

									return num < Component.pages() && (
										<Button size="narrow" key={i} onClick={()=>Component.page(num)} disabled={ page == num }>{ num + 1 }</Button>
									);

								})
							}
							<Button size="narrow" onClick={()=>Component.next()} disabled={ page == ( Component.pages() - 1 ) } arrow>{ t('Navigation.next') }</Button>
						</div>
					)}
				</div>
			</div>
		</section>
	);

}